import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useGetResidentialRatesQuery } from "../../../store/services/ContentService";
import { clearGuest } from "../../../store/slices/guestsSlice";
import { showModal } from "../../../store/slices/modalSlice";
import { Market } from "../../../store/slices/searchSlice";
import { useTypedDispatch, useTypedSelector } from "../../../store/store";
import MODAL from "../../../utils/constants/modal";
import { currencyToFormat } from "../../../utils/helpers/currency";
import { insertValuesToString } from "../../../utils/helpers/insertValuesToString";
import {
  getCruiseMinPrice,
  getMarketPrice,
} from "../../../utils/helpers/priceUtils";
import { validateRooms } from "../../../utils/validation";
import LoadingContainer from "../../containers/LoadingContainer";
import Button from "../../shared/Button";
import CustomCarousel from "../../shared/Carousel";
import Counter from "../../shared/Counter";

import styles from "./index.module.scss";

type InputKey = keyof IInputs;

type InputChangeHandler = {
  value: string;
  valueKey: InputKey;
};

type CounterChangePayload = { value: string; valueKey?: string };

interface IValidationInputs {
  rooms: string;
  rate?: string;
}

interface IInputs {
  rooms: { value: string; errorMessage: string };
  rate?: { value: string; errorMessage: string };
}

function DummyCruise() {
  const { t } = useTranslation();

  return <p>{t("Cruise not found")}</p>;
}

function Cruise() {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { pathname, search } = useLocation();

  const { cruise, isCruiseLoading } = useTypedSelector((state) => state.search);

  const {
    enable_resedential_rate,
    resedential_rate_required,
    resedential_rate_label,
  } = useTypedSelector((state) => state.environment);

  const { data, isFetching } = useGetResidentialRatesQuery(null, {
    skip: !enable_resedential_rate,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();

  const rates = [{ code: "", name: "" }, ...(data || [])];

  const {
    date_format,
    subtract_gft,
    number_stateroom_page_description,
    gft_additional_text,
  } = useTypedSelector((state) => state.environment);

  const [inputs, setInputs] = useState<IInputs>({
    rooms: { value: "1", errorMessage: "" },
    rate: { value: "", errorMessage: "" },
  });

  const price = useMemo(() => {
    if (!cruise) {
      return { price: 0, gft: 0, currency: "USD" };
    }

    const minPrice = getCruiseMinPrice(cruise, subtract_gft);

    const marketEntries = Object.entries(cruise.markets).filter(
      ([key, market]) =>
        key !== "currency" && typeof market === "object" && market !== null,
    ) as Array<[string, Market]>;

    if (marketEntries.length === 0) {
      return {
        price: minPrice,
        gft: 0,
        currency: cruise.pricing?.[0]?.currency ?? "USD",
      };
    }

    const marketWithMinPrice = marketEntries
      .map(([_, market]) => market)
      .reduce((minMarket, currentMarket) => {
        const currentPrice = getMarketPrice(currentMarket, false);
        const minMarketPrice = getMarketPrice(minMarket, false);

        return currentPrice < minMarketPrice && currentPrice > 0
          ? currentMarket
          : minMarket;
      });

    const gft = parseFloat(marketWithMinPrice?.gft || "0");

    return {
      price: minPrice,
      gft,
      currency: cruise.pricing?.[0]?.currency ?? "USD",
    };
  }, [cruise, subtract_gft]);

  const additionalGftText = useMemo(
    () =>
      insertValuesToString(gft_additional_text, {
        gft: currencyToFormat(price.gft, price.currency ?? "USD"),
      }),
    [price, gft_additional_text],
  );

  const handleShowModal = () => {
    dispatch(
      showModal({
        type: MODAL.MODAL_TYPES.ITINERARY,
        size: MODAL.MODAL_SIZES.LG,
      }),
    );
  };

  const handleInputChange = ({ value, valueKey }: InputChangeHandler) => {
    setInputs((prev) => ({
      ...prev,
      [valueKey]: { errorMessage: "", value },
    }));
  };

  const handleCounterChange = ({ value, valueKey }: CounterChangePayload) => {
    if (valueKey !== "rooms" && valueKey !== "rate") {
      return;
    }

    handleInputChange({ value, valueKey });
  };

  const handleFormError = (errors: IValidationInputs) => {
    setInputs((prev) => {
      const updatedInputs: IInputs = { ...prev };

      (Object.keys(errors) as Array<keyof IValidationInputs>).forEach(
        (errorKey) => {
          if (errorKey === "rooms") {
            updatedInputs.rooms = {
              ...updatedInputs.rooms,
              errorMessage: errors.rooms ?? "",
            };
          }

          if (errorKey === "rate" && updatedInputs.rate) {
            updatedInputs.rate = {
              ...updatedInputs.rate,
              errorMessage: errors.rate ?? "",
            };
          }
        },
      );

      return updatedInputs;
    });
  };

  const handleSubmit = () => {
    const data = {
      rooms: inputs.rooms.value,
      rate: inputs.rate?.value,
      resedential_rate_required:
        resedential_rate_required && enable_resedential_rate,
    };

    if (!resedential_rate_required || !enable_resedential_rate) {
      delete data.rate;
    }

    validateRooms({
      data,
      onSuccess: async (validData: IValidationInputs) => {
        const residency =
          enable_resedential_rate && inputs.rate?.value
            ? inputs.rate.value
            : "";

        const residencyPart = residency
          ? `&residency=${encodeURIComponent(residency)}`
          : "";

        navigate(
          `${pathname}/rooms/${search}&rooms=${validData.rooms}&room=1${residencyPart}`,
        );

        window.scrollTo(0, 0);
      },
      onError: (errors: IValidationInputs) => handleFormError(errors),
    });
  };

  if (!isCruiseLoading && !cruise) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <DummyCruise />
        </div>
      </div>
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    dispatch(clearGuest());
  }, [dispatch]);

  if (!isCruiseLoading && !cruise) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <DummyCruise />
        </div>
      </div>
    );
  }

  return (
    <LoadingContainer isLoading={isCruiseLoading}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.imagesContainer}>
            <CustomCarousel
              items={cruise?.ship.images ?? []}
              className={styles.carousel}
            />

            <Button
              className={styles.itineraryButton}
              label={t("view itinerary")}
              variant="secondary"
              icon="FiPlus"
              onClick={handleShowModal}
            />

            <p className={styles.name}>{cruise?.cruise.name ?? ""}</p>
          </div>

          <table className={styles.details}>
            <tbody>
              <tr>
                <td>{t("SHIP NAME")}</td>
                <td>{cruise?.ship.name}</td>
              </tr>

              <tr>
                <td>{t("DEPARTS")}</td>
                <td>{dayjs(cruise?.embark ?? "").format(date_format)}</td>
              </tr>

              <tr>
                <td>{t("NIGHTS")}</td>
                <td>{cruise?.cruise.nights}</td>
              </tr>
            </tbody>
          </table>

          <div className={styles.price}>
            <span className={styles.price_from}>{t("From")}</span>

            <span className={styles.price_value}>
              {currencyToFormat(price.price, cruise?.markets.currency ?? "USD")}
              &nbsp;pp
            </span>
          </div>

          <div className={styles.staterooms}>
            <p className={styles.staterooms_title}>
              {t("How many staterooms do you need?")}
            </p>

            <span className={styles.staterooms_subtitle}>
              {number_stateroom_page_description || ""}
            </span>

            <div className={styles.staterooms_config}>
              <Counter
                value={inputs.rooms.value}
                valueKey="rooms"
                errorMessage={inputs.rooms.errorMessage}
                min={1}
                max={3}
                onChange={handleCounterChange}
              />

              {enable_resedential_rate && (
                <>
                  {isFetching ? (
                    <CircularProgress />
                  ) : (
                    <FormControl
                      fullWidth
                      required={resedential_rate_required}
                      error={!!inputs.rate?.errorMessage}>
                      <InputLabel
                        id="rate-label"
                        sx={{
                          fontFamily: "Montserrat, sans-serif",
                          color: "var(--input-placeholder-color, #8C8C8C)",

                          "&.Mui-focused": {
                            color: "var(--input-focus-border-color, #666666)",
                          },
                        }}>
                        {t(resedential_rate_label)}
                      </InputLabel>

                      <Select
                        labelId="rate-label"
                        label={t(resedential_rate_label)}
                        value={inputs.rate?.value}
                        onChange={(e) =>
                          handleInputChange({
                            value: e.target.value,
                            valueKey: "rate",
                          })
                        }
                        sx={{ borderRadius: "0", height: "46px" }}
                        MenuProps={{
                          PaperProps: { sx: { borderRadius: 0 } },
                          MenuListProps: { sx: { py: 0 } },
                        }}>
                        {rates.map((item) => (
                          <MenuItem
                            key={item.code}
                            value={item.code}
                            sx={{
                              fontFamily: "Montserrat, sans-serif",
                              "&.MuiButtonBase-root.MuiMenuItem-root.MuiMenuItem-gutters":
                                { minHeight: "33px" },
                            }}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>

                      <FormHelperText>
                        {inputs.rate?.errorMessage}
                      </FormHelperText>
                    </FormControl>
                  )}
                </>
              )}
            </div>
          </div>

          <Button
            label={t("continue to staterooms")}
            className={styles.submitButton}
            onClick={handleSubmit}
          />

          {gft_additional_text && (
            <span className={styles.footerNote}>{additionalGftText}</span>
          )}
        </div>
      </div>
    </LoadingContainer>
  );
}

export default Cruise;
